// common/services/socialFeed/getSocialFeedRecipients.ts
import req from '../../api/request'

/**
 * Where a recipient stands, and whether we can actually reach them (SN-7901).
 *
 * The first three are the ROSTER's own words. The team page has said Invited / Accepted
 * for years, and this list used to invent its own vocabulary for the same fact — which is
 * the inconsistency James called out on the Sep 3 call, not the choice of words itself.
 *
 *  joined       — Accepted: holds an account
 *  invited      — Invited: no account, invitation sent and not accepted
 *  not_invited  — Not Invited: no account, no invitation ever raised
 *  no_email     — no address anywhere; cannot be reached at all
 *
 * Four, since the Sep 4 QA call. `guardian_linked` and `email_opted_out` were folded into
 * `joined` server-side: both describe somebody who IS reached, and this screen answers
 * "who am I about to email". `no_email` survives because it is the only status that
 * answers the other half of that question — who am I missing — and the roster has no word
 * for it, so it keeps its own.
 *
 * Note the cost of the fold, since nothing on screen shows it any more: a sender can no
 * longer see who muted social-communication email. `reach.email_joined` still excludes
 * them, so no number lies.
 */
export type RecipientStatus = 'joined' | 'invited' | 'not_invited' | 'no_email'

export type SocialFeedRecipient = {
  name_first: string | null
  name_last: string | null
  email: string
  group: string
  status: RecipientStatus
}

export type SocialFeedRecipientsResponse = {
  data: SocialFeedRecipient[]
  meta: {
    pagination: {
      total: number
      count: number
      per_page: number
      current_page: number
      total_pages: number
    }
    totals: Record<RecipientStatus | 'all', number>
  }
}

export type SocialFeedRecipientsParams = {
  entityId: string
  entityType: 'organization' | 'team' | 'competition'
  roleTypes: number[]
  activeCompetition: boolean
  includeUnjoined: boolean
  page?: number
  perPage?: number
  status?: RecipientStatus | null
  q?: string
}

const buildBody = ({
  roleTypes,
  activeCompetition,
  includeUnjoined,
  page,
  perPage,
  status,
  q,
}: SocialFeedRecipientsParams) => ({
  role_types: roleTypes,
  active_competition: activeCompetition,
  include_unjoined: includeUnjoined,
  ...(page ? { page } : {}),
  ...(perPage ? { per_page: perPage } : {}),
  ...(status ? { status } : {}),
  ...(q ? { q } : {}),
})

/**
 * One page of "who am I about to email".
 *
 * POST rather than GET because it takes the same criteria body as `audience_size` and has
 * to answer for a draft that has not been saved yet.
 */
export const getSocialFeedRecipients = async (
  params: SocialFeedRecipientsParams
): Promise<SocialFeedRecipientsResponse> => {
  const { entityId, entityType } = params

  return (await req(`/social-feed/${entityId}/${entityType}/recipients`, {
    method: 'POST',
    body: JSON.stringify(buildBody(params)),
  })) as SocialFeedRecipientsResponse
}

/**
 * Download the whole recipient set as CSV — every status, ignoring paging and the status
 * filter, so one file answers both "who did I reach" and "who did I miss".
 *
 * `parseJSON: false` hands back the raw Response. Without it `req` calls `.json()` on a
 * text/csv body and throws.
 */
export const downloadSocialFeedRecipientsCsv = async (
  params: SocialFeedRecipientsParams,
  fileName: string
): Promise<void> => {
  const { entityId, entityType } = params

  const response = await req(
    `/social-feed/${entityId}/${entityType}/recipients`,
    {
      method: 'POST',
      body: JSON.stringify({ ...buildBody(params), format: 'csv' }),
      parseJSON: false,
    }
  )

  const blob = await (response as Response).blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = downloadUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(downloadUrl)
}
